import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { mediaAPI, resolveMediaSrc } from '@/lib/api';
import { toast } from 'sonner';

const MusicPlayerContext = createContext(null);

export const useMusicPlayer = () => {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  return ctx;
};

export const MusicPlayerProvider = ({ studentId, children }) => {
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('none'); // none, one, all
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef(null);
  const timeUpdateRef = useRef(null);

  const currentTrack = currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;

  // Audio element setup
  useEffect(() => {
    const audio = new Audio();
    audio.volume = volume;
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('ended', () => handleTrackEnd());
    audio.addEventListener('error', () => {
      setIsPlaying(false);
    });
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.removeAttribute('src');
      if (timeUpdateRef.current) cancelAnimationFrame(timeUpdateRef.current);
    };
  }, []);

  // Time update loop (more efficient than timeupdate event)
  useEffect(() => {
    const updateTime = () => {
      if (audioRef.current && isPlaying) {
        setCurrentTime(audioRef.current.currentTime);
      }
      timeUpdateRef.current = requestAnimationFrame(updateTime);
    };
    if (isPlaying) {
      timeUpdateRef.current = requestAnimationFrame(updateTime);
    }
    return () => {
      if (timeUpdateRef.current) cancelAnimationFrame(timeUpdateRef.current);
    };
  }, [isPlaying]);

  const handleTrackEnd = useCallback(() => {
    if (repeat === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else if (currentIndex < queue.length - 1) {
      playIndex(shuffle ? Math.floor(Math.random() * queue.length) : currentIndex + 1);
    } else if (repeat === 'all' && queue.length > 0) {
      playIndex(0);
    } else {
      setIsPlaying(false);
    }
  }, [repeat, shuffle, currentIndex, queue.length]);

  // Re-attach ended handler when deps change
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handler = () => handleTrackEnd();
    audio.removeEventListener('ended', handler);
    audio.addEventListener('ended', handler);
    return () => audio.removeEventListener('ended', handler);
  }, [handleTrackEnd]);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const playIndex = useCallback(async (idx) => {
    if (idx < 0 || idx >= queue.length) return;
    const track = queue[idx];
    const audio = audioRef.current;
    if (track.youtube_url) return; // YouTube handled separately in full player
    let src;
    try { src = await resolveMediaSrc(track.file_url); } catch (e) { toast.info(e.userMessage || 'This song is not available right now.'); return; }

    audio.src = src;
    audio.play().catch(() => {});
    setCurrentIndex(idx);
    setIsPlaying(true);
    setCurrentTime(0);

    // Record listen
    if (studentId && track.media_id) {
      mediaAPI.recordListen(studentId, track.media_id).catch(() => {});
    }
  }, [queue, studentId, API_BASE]);

  const play = useCallback(() => {
    if (currentTrack) {
      audioRef.current?.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [currentTrack]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause(); else play();
  }, [isPlaying, play, pause]);

  const next = useCallback(() => {
    if (queue.length === 0) return;
    const nextIdx = shuffle
      ? Math.floor(Math.random() * queue.length)
      : (currentIndex + 1) % queue.length;
    playIndex(nextIdx);
  }, [queue.length, currentIndex, shuffle, playIndex]);

  const previous = useCallback(() => {
    if (queue.length === 0) return;
    // If more than 3 seconds in, restart current track
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    const prevIdx = currentIndex > 0 ? currentIndex - 1 : queue.length - 1;
    playIndex(prevIdx);
  }, [queue.length, currentIndex, playIndex]);

  const seek = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const setVolumeLevel = useCallback((v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  // Queue management
  const playTrack = useCallback((track) => {
    // Add to front of queue and play immediately
    setQueue(prev => {
      const filtered = prev.filter(t => t.media_id !== track.media_id);
      const newQueue = [track, ...filtered];
      setTimeout(() => playIndex(0), 0);
      return newQueue;
    });
  }, [playIndex]);

  const addToQueue = useCallback((track) => {
    setQueue(prev => {
      if (prev.some(t => t.media_id === track.media_id)) return prev;
      return [...prev, track];
    });
  }, []);

  const removeFromQueue = useCallback((index) => {
    setQueue(prev => {
      const newQueue = [...prev];
      newQueue.splice(index, 1);
      return newQueue;
    });
    if (index < currentIndex) setCurrentIndex(prev => prev - 1);
    if (index === currentIndex) {
      setIsPlaying(false);
    }
  }, [currentIndex]);

  const playAll = useCallback(async (tracks) => {
    setQueue(tracks);
    setCurrentIndex(0);
    const audio = audioRef.current;
    const track = tracks[0];
    if (track && track.file_url) {
      try { audio.src = await resolveMediaSrc(track.file_url); } catch (e) { toast.info(e.userMessage || 'This song is not available right now.'); return; }
      audio.play().catch(() => {});
      setIsPlaying(true);
      if (studentId && track.media_id) {
        mediaAPI.recordListen(studentId, track.media_id).catch(() => {});
      }
    }
  }, [studentId, API_BASE]);

  const clearQueue = useCallback(() => {
    audioRef.current?.pause();
    setQueue([]);
    setCurrentIndex(-1);
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const value = {
    // State
    queue, currentIndex, currentTrack, isPlaying, duration, currentTime, volume, shuffle, repeat, expanded,
    // Controls
    play, pause, togglePlay, next, previous, seek, setVolumeLevel,
    setShuffle: () => setShuffle(p => !p),
    setRepeat: () => setRepeat(p => p === 'none' ? 'all' : p === 'all' ? 'one' : 'none'),
    setExpanded,
    // Queue
    playTrack, addToQueue, removeFromQueue, playAll, clearQueue,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};
